import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';
import { Select } from '../../components/ui/controls';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { ApiError } from '../../lib/api';
import { useBulkUpdateCollection } from './queries';

interface BulkEditDialogProps {
  open: boolean;
  onClose: () => void;
  ids: string[];
  knownLocations: string[];
  onDone: () => void;
}

type ReadChoice = 'keep' | 'read' | 'unread';

/** Sets location or the read flag on a whole selection at once. */
export function BulkEditDialog({
  open,
  onClose,
  ids,
  knownLocations,
  onDone,
}: BulkEditDialogProps) {
  const { t } = useTranslation();
  const fieldIds = useId();
  const bulkUpdate = useBulkUpdateCollection();

  const [location, setLocation] = useState('');
  const [changeLocation, setChangeLocation] = useState(false);
  const [readChoice, setReadChoice] = useState<ReadChoice>('keep');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLocation('');
    setChangeLocation(false);
    setReadChoice('keep');
    setError(null);
  }, [open]);

  async function submit() {
    if (!changeLocation && readChoice === 'keep') {
      setError(t('bulk.nothingSelected'));
      return;
    }

    try {
      await bulkUpdate.mutateAsync({
        ids,
        ...(changeLocation ? { location: location.trim() || null } : {}),
        ...(readChoice === 'keep' ? {} : { isRead: readChoice === 'read' }),
      });
      onDone();
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('bulk.title')}
      description={t('bulk.description', { count: ids.length })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={bulkUpdate.isPending}>
            {t('form.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={bulkUpdate.isPending}>
            {bulkUpdate.isPending ? t('form.saving') : t('bulk.apply')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('collection.location')} htmlFor={`${fieldIds}-location`}>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={changeLocation}
              onChange={(event) => setChangeLocation(event.target.checked)}
              aria-label={t('bulk.changeLocation')}
              className="size-5 rounded border bg-surface accent-[var(--pv-accent)]"
            />
            <Input
              id={`${fieldIds}-location`}
              list={`${fieldIds}-locations`}
              value={location}
              disabled={!changeLocation}
              placeholder={t('bulk.locationPlaceholder')}
              onChange={(event) => setLocation(event.target.value)}
            />
            <datalist id={`${fieldIds}-locations`}>
              {knownLocations.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </div>
        </Field>

        <Field label={t('collection.isRead')} htmlFor={`${fieldIds}-read`}>
          <Select
            id={`${fieldIds}-read`}
            value={readChoice}
            onChange={(event) => setReadChoice(event.target.value as ReadChoice)}
          >
            <option value="keep">{t('bulk.keep')}</option>
            <option value="read">{t('bulk.markRead')}</option>
            <option value="unread">{t('bulk.markUnread')}</option>
          </Select>
        </Field>

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
